import React from 'react';
import { createRoot } from 'react-dom/client';

import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import '@fontsource/dosis';
import '@fontsource/roboto';
import CssBaseline from '@mui/material/CssBaseline';

import theme from './theme';
import RestreamerUI from './RestreamerUI';

/**
 * Resolve the Restreamer Core address:
 *
 * 1. ?address=                        → explicit URL param (always wins)
 * 2. process.env.REACT_APP_CORE_URL   → .env / .env.local (CRA injects at build time)
 *                                       Prefer the explicit build-time value so the UI
 *                                       connects to the configured Core directly.
 * 3. window.__RESTREAMER_CONFIG__      → public/config.js (Docker/production runtime)
 * 4. /ui/ path detection              → strip /ui/ suffix (embedded inside Core)
 * 5. window.location.origin           → same-origin fallback (production same host)
 */
const urlParams = new URLSearchParams(window.location.search.substring(1));
const runtimeConfig = window.__RESTREAMER_CONFIG__ || {};

let address;

if (urlParams.has('address')) {
	// 1. Explicit override via URL param
	address = urlParams.get('address');
} else if (process.env.REACT_APP_CORE_URL && process.env.REACT_APP_CORE_URL.trim() !== '') {
	// 2. .env / .env.local — CRA injects at build time.
	//    Prefer the explicit REACT_APP_CORE_URL build-time value so the UI
	//    connects to the configured Core directly instead of relying on
	//    runtime config. This ensures the UI respects the user's
	//    REACT_APP_CORE_URL setting from .env.local or the build environment.
	address = process.env.REACT_APP_CORE_URL.trim().replace(/\/$/, '');
} else if (runtimeConfig.CORE_ADDRESS && runtimeConfig.CORE_ADDRESS.trim() !== '') {
	// 3. Runtime config.js — Docker / production without /ui/ path
	address = runtimeConfig.CORE_ADDRESS.trim().replace(/\/$/, '');
} else if (window.location.pathname.includes('/ui/')) {
	// 4. Embedded inside Core at /ui/ path
	address = window.location.protocol + '//' + window.location.host +
		window.location.pathname.replace(/\/ui\/.*$/, '');
} else {
	// 5. Same-origin production (Core and UI on the same host/port)
	address = window.location.origin;
}

createRoot(document.getElementById('root')).render(
	<StyledEngineProvider injectFirst>
		<ThemeProvider theme={theme}>
			<CssBaseline />
			<RestreamerUI address={address} />
		</ThemeProvider>
	</StyledEngineProvider>
);
